import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { site } from "../lib/site";
import { IconMenu, IconPhone, IconX } from "./Icon";
import { BrandMark } from "./BrandMark";

const links = [
  { to: "/", label: "Accueil", end: true },
  { to: "/services", label: "Services" },
  { to: "/zones-intervention", label: "Zones" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();

  useEffect(() => setOpen(false), [location.pathname]);

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    setScrolled(latest > 12);
    if (latest > 140 && latest > previous + 4) setHidden(true);
    else if (latest < previous - 2 || latest < 100) setHidden(false);
  });

  return (
    <motion.header
      initial={{ y: 0 }}
      animate={{ y: hidden && !open ? -96 : 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-40 will-change-transform"
    >
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-brand-700 focus:px-3 focus:py-2 focus:text-white"
      >
        Aller au contenu
      </a>

      <motion.div
        animate={{
          paddingTop: scrolled ? 12 : 20,
          paddingBottom: scrolled ? 12 : 20,
        }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="container-page">
          <motion.div
            animate={{
              backgroundColor: scrolled ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0)",
              borderColor: scrolled ? "rgba(186,230,253,0.6)" : "rgba(186,230,253,0)",
              boxShadow: scrolled
                ? "0 12px 40px -12px rgba(8,145,178,0.22)"
                : "0 0 0 rgba(0,0,0,0)",
            }}
            transition={{ duration: 0.25 }}
            className={`flex items-center justify-between rounded-full border backdrop-blur-xl px-3 sm:px-4 lg:px-5 h-14 ${
              scrolled ? "" : "border-transparent"
            }`}
            style={{ WebkitBackdropFilter: "blur(16px)" }}
          >
            <Link
              to="/"
              className="flex items-center gap-2.5 group pl-1"
              aria-label={`${site.name} - accueil`}
            >
              <BrandMark className="h-10 w-10 object-contain transition group-hover:opacity-80" />
              <div className="leading-tight hidden sm:block">
                <div className="font-display font-bold text-brand-900">{site.name}</div>
                <div className="text-[11px] text-ink-muted -mt-0.5">
                  Nettoyage professionnel · Vichy
                </div>
              </div>
            </Link>

            <nav
              className="hidden lg:flex items-center gap-1"
              aria-label="Navigation principale"
            >
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `relative px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
                      isActive
                        ? "text-brand-900"
                        : "text-ink-muted hover:text-brand-800"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-full bg-brand-50"
                          transition={{ type: "spring", stiffness: 400, damping: 32 }}
                        />
                      )}
                      <span className="relative">{l.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-2">
              <a
                href={site.phoneHref}
                className="btn btn-ghost btn-sm"
                aria-label={`Appeler ${site.name}`}
              >
                <IconPhone className="h-4 w-4" />
                {site.phoneDisplay}
              </a>
              <Link to="/contact" className="btn-primary btn-sm">
                Devis gratuit
              </Link>
            </div>

            <button
              type="button"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-800 shadow-soft cursor-pointer transition-colors hover:bg-brand-50"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((v) => !v)}
            >
              <motion.div
                animate={{ rotate: open ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {open ? <IconX className="h-5 w-5" /> : <IconMenu className="h-5 w-5" />}
              </motion.div>
            </button>
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden container-page mt-2"
          >
            <div className="rounded-3xl bg-white/95 backdrop-blur-xl border border-brand-100 shadow-soft-lg overflow-hidden">
              <nav className="p-3 flex flex-col gap-1" aria-label="Navigation mobile">
                {links.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.end}
                      className={({ isActive }) =>
                        `block px-4 py-3 rounded-xl text-base font-medium ${
                          isActive
                            ? "bg-brand-50 text-brand-900"
                            : "text-ink hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <div className="mt-3 grid grid-cols-2 gap-2 p-1">
                  <a href={site.phoneHref} className="btn-ghost justify-center">
                    <IconPhone className="h-4 w-4" /> Appeler
                  </a>
                  <Link to="/contact" className="btn-primary justify-center">
                    Devis gratuit
                  </Link>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
